import { useMemo, type ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import { useConfig, type IApp } from '@ohpen/react-utils';
import { IconSettings } from '@tabler/icons-react';

import { DEFAULT_SECTION_PATH } from '@config';

/** How PMP identifies itself to the configuration module. */
export const PMP_APPLICATION_ID = 'pmp';

/** Absolute origin of the configuration application once it is deployed on its own. */
interface ConfigAppUrl extends IApp {
  configUrl?: string;
}

/**
 * A link rather than a navigate() call, so the same code works while configuration is
 * mounted inside PMP and after it moves to an origin of its own.
 */
export const ConfigLink = (): ReactElement => {
  const { t } = useTranslation();
  const { APP } = useConfig<ConfigAppUrl>();
  const baseUrl = APP?.configUrl;
  const location = useLocation();

  // Crossing to another origin means the module cannot resolve a bare path against this one,
  // so the way back has to name the origin. Same origin keeps the path, which is what makes
  // `?returnTo=` unusable as a redirect while configuration is mounted here.
  const path = useMemo(() => {
    const here = `${location.pathname}${location.search}`;
    const returnTo = encodeURIComponent(baseUrl ? `${globalThis.location.origin}${here}` : here);
    return `/config/${DEFAULT_SECTION_PATH}?returnTo=${returnTo}`;
  }, [baseUrl, location.pathname, location.search]);

  const label = t('global.labels.configuration');
  const icon = <IconSettings size={20} strokeWidth={1.5} />;

  return (
    <Tooltip title={label}>
      {baseUrl ? (
        <a href={`${baseUrl}${path}`} aria-label={label}>
          {icon}
        </a>
      ) : (
        <Link to={path} aria-label={label}>
          {icon}
        </Link>
      )}
    </Tooltip>
  );
};
